import { API } from './api.config';
import { API_URL } from './api-url.config';
import { safeJSONParse } from './safeJwts';

const ENDPOINT = 'platform-analytics/api/v1/analytics';

// A timeupdate tick arrives roughly every 250ms. Apparent progress beyond this
// is a seek, a rewind, or a resume after a stall — never content the viewer sat
// through, so it must not count towards watched time.
const MAX_TICK_SECONDS = 2;

/**
 * Accumulates genuinely-watched seconds for one playback session.
 *
 * `duration` is content actually consumed, not the distance between two
 * playheads: seeking from 0:10 to 12:00 and watching two seconds reports two,
 * not twelve minutes.
 */
export function createPlaybackTracker() {
  let lastPosition = null;
  let watched = 0;

  return {
    /** Feed every timeupdate while content (not an ad) is really playing. */
    sample(position) {
      if (typeof position !== 'number' || Number.isNaN(position)) {
        return;
      }
      if (lastPosition !== null) {
        const delta = position - lastPosition;
        if (delta > 0 && delta <= MAX_TICK_SECONDS) {
          watched += delta;
        }
      }
      lastPosition = position;
    },

    /**
     * Close the open segment and hand it over.
     *
     * @param {number} [position] Where the viewer left off. Omit it on a seek:
     *        player.currentTime() already reports the seek *target* by the time
     *        the event fires, so the position watched up to is the last sample.
     * @returns {{playbackPosition: number, duration: number}|null} null when
     *          nothing was watched, which makes repeated flushes harmless.
     */
    take(position) {
      const at = typeof position === 'number' && !Number.isNaN(position) ? position : lastPosition;
      const duration = Math.round(watched);

      // Always close, so the next segment starts from the new playhead and a
      // short seek cannot leak its skipped seconds into the following one.
      watched = 0;
      lastPosition = null;

      if (at === null || duration < 1) {
        return null;
      }
      return { playbackPosition: Math.floor(at), duration };
    }
  };
}

/**
 * POST one watched segment.
 *
 * @param {object} payload
 * @param {{ keepalive?: boolean }} [options] Set keepalive when the page is
 *        going away: an axios request rides on XHR, which the browser cancels
 *        on unload, losing the final segment of every session.
 */
export function postPlaybackData(payload, { keepalive } = {}) {
  if (!keepalive) {
    API.post(ENDPOINT, payload).catch((error) => {
      console.error('Error posting playback analytics', error);
    });
    return;
  }

  try {
    const title = window.platform_info.title;
    const token =
      safeJSONParse(localStorage.getItem(`access_token_r_${title}`)) ||
      safeJSONParse(localStorage.getItem(`access_token_temp_${title}`));

    fetch(`${API_URL}${ENDPOINT}`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify(payload)
    }).catch(() => {});
  } catch (error) {
    console.error('Error posting playback analytics on unload', error);
  }
}
